import React, { forwardRef } from "react";
import { makeStyles } from "@material-ui/core";
import {
  SnackbarProvider as SnackbarProviderInternal,
  SnackbarContent,
  useSnackbar as useSnackbarInternal,
  SnackbarMessage,
  OptionsObject,
  SnackbarKey,
  ProviderContext,
} from "notistack";
import {
  SemanticVariantTypes,
  HvSnackbarContent as HvSnackbarContentWrapper,
} from "@hitachivantara/uikit-react-core";

// this override allow us to inject the variant in the custom snackbar, since it is not supported by notistack
export const useSnackbar = (): ProviderContext => {
  const { enqueueSnackbar: enqueueSnackbarInternal, ...others } =
    useSnackbarInternal();

  const enqueueSnackbar = (
    message: SnackbarMessage,
    options?: OptionsObject
  ): SnackbarKey =>
    enqueueSnackbarInternal(
      JSON.stringify({ message, variant: options?.variant }),
      options
    );

  return {
    ...others,
    enqueueSnackbar,
  };
};

const useStyles = makeStyles(() => ({
  containerAnchorOriginTopRight: {
    // override notistack default notifications root
    top: "60px !important",
    right: "30px !important",
    zIndex: 10000000,
  },
}));

const SnackMessage = forwardRef<
  HTMLDivElement,
  {
    message: string | React.ReactNode;
    variant?: SemanticVariantTypes;
  }
>(({ message, variant = "success" }, ref) => (
  <SnackbarContent ref={ref}>
    <HvSnackbarContentWrapper
      variant={variant}
      showIcon
      label={message as string}
    />
  </SnackbarContent>
));

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { containerAnchorOriginTopRight } = useStyles();

  return (
    <SnackbarProviderInternal
      classes={{
        containerAnchorOriginTopRight,
      }}
      maxSnack={5}
      autoHideDuration={5000}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      content={(key, messageProp) => {
        const { message, variant } = JSON.parse(messageProp as string);
        return <SnackMessage message={message} variant={variant} />;
      }}
    >
      {children}
    </SnackbarProviderInternal>
  );
};
